import { Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';

interface Offset {
  right: number;
  bottom: number;
}

/**
 * A drag handle for the HubSpot chat bubble. The bubble is HubSpot's cross-origin iframe, so it cannot be
 * dragged itself: this grip sits on its corner and moves HubSpot's container. The spot is remembered per
 * browser, kept on screen, and the open HubSpot chat is pulled back on screen when it grows.
 */
@Component({
  selector: 'app-hubspot-drag',
  templateUrl: './hubspot-drag.component.html',
  styleUrls: ['./hubspot-drag.component.scss'],
})
export class HubspotDragComponent implements OnInit, OnDestroy {
  static readonly POSITION_KEY = 'hubspot_launcher_position';
  private static readonly CONTAINER_ID = 'hubspot-messages-iframe-container';
  /** Above this height HubSpot's chat window is open: the grip is hidden then. */
  private static readonly CLOSED_MAX_HEIGHT = 200;

  handle: { left: number; top: number } | null = null;
  isDragging = false;
  private offset: Offset | null = null;
  private dragStart: { x: number; y: number; right: number; bottom: number } | null = null;
  private timer: any;
  private resizeObserver: ResizeObserver | null = null;
  private observed: HTMLElement | null = null;

  constructor(private zone: NgZone) {}

  ngOnInit(): void {
    this.offset = this.loadOffset();
    // HubSpot adds its container some seconds after the page loads (and again after navigation).
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => this.zone.run(() => this.sync()), 700);
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.timer);
    this.resizeObserver?.disconnect();
  }

  /** The area fixed elements are placed in (without scrollbars, unlike window.innerWidth). */
  private get viewWidth(): number {
    return document.documentElement.clientWidth || window.innerWidth;
  }

  private get viewHeight(): number {
    return document.documentElement.clientHeight || window.innerHeight;
  }

  private get container(): HTMLElement | null {
    return document.getElementById(HubspotDragComponent.CONTAINER_ID);
  }

  /** Applies the saved spot to HubSpot's container and puts the grip on the bubble's corner. */
  sync(): void {
    const el = this.container;
    if (!el || el.getBoundingClientRect().width === 0) {
      this.handle = null;
      return;
    }
    if (el !== this.observed && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.zone.run(() => this.sync()));
      this.resizeObserver.observe(el);
      this.observed = el;
    }
    if (this.offset) {
      this.apply(el, this.clamp(el, this.offset));
    }
    const rect = el.getBoundingClientRect();
    // Left of the bubble, level with its middle, outside HubSpot's box (which would cover it) and clear of
    // the WizeHelp launcher above it.
    this.handle = rect.height > HubspotDragComponent.CLOSED_MAX_HEIGHT
      ? null
      : { left: Math.max(rect.left - 26, 0), top: Math.max(rect.top + rect.height / 2 - 12, 0) };
  }

  onPointerDown(event: PointerEvent): void {
    const el = this.container;
    if (event.button !== 0 || !el) {
      return;
    }
    event.preventDefault();
    HubspotDragComponent.capture(event);
    const rect = el.getBoundingClientRect();
    this.dragStart = { x: event.clientX, y: event.clientY, right: this.viewWidth - rect.right, bottom: this.viewHeight - rect.bottom };
    this.isDragging = true;
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    const el = this.container;
    if (!this.dragStart || !el) {
      return;
    }
    event.preventDefault();
    this.offset = this.clamp(el, {
      right: this.dragStart.right - (event.clientX - this.dragStart.x),
      bottom: this.dragStart.bottom - (event.clientY - this.dragStart.y),
    });
    this.apply(el, this.offset);
    this.sync();
  }

  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  onPointerUp(): void {
    if (!this.dragStart) {
      return;
    }
    this.dragStart = null;
    this.isDragging = false;
    try {
      localStorage.setItem(HubspotDragComponent.POSITION_KEY, JSON.stringify(this.offset));
    } catch {
      // Not remembered, still moved for this visit.
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.sync();
  }

  /**
   * Keeps the pointer's events on the grip while dragging: without it the HubSpot iframe (which moves under
   * the pointer) swallows the moves and the drag stops.
   */
  static capture(event: PointerEvent): void {
    try {
      (event.target as Element | null)?.setPointerCapture?.(event.pointerId);
    } catch {
      // Not supported: dragging still works away from iframes.
    }
  }

  /**
   * Right edge of the app's left menu while it is shown (0 when hidden or collapsed to nothing): launchers are
   * kept to its right so they never slide under it.
   */
  static sidebarRight(): number {
    const sidebar = document.getElementById('sidebar');
    if (!sidebar) {
      return 0;
    }
    const rect = sidebar.getBoundingClientRect();
    const style = getComputedStyle(sidebar);
    if (rect.width === 0 || rect.right <= 0 || style.display === 'none' || style.visibility === 'hidden') {
      return 0;
    }
    return Math.max(rect.right, 0);
  }

  /** Keeps the whole container (bubble, or the open chat window) inside the screen and right of the menu. */
  private clamp(el: HTMLElement, offset: Offset): Offset {
    const rect = el.getBoundingClientRect();
    // The grip sits 26px left of the container: keep that clear of the menu too.
    const left = HubspotDragComponent.sidebarRight() + 30;
    return {
      right: Math.round(Math.min(Math.max(offset.right, 0), Math.max(this.viewWidth - rect.width - left, 0))),
      bottom: Math.round(Math.min(Math.max(offset.bottom, 0), Math.max(this.viewHeight - rect.height, 0))),
    };
  }

  /** Inline !important wins over HubSpot's own styles and the bottom offset in styles.scss. */
  private apply(el: HTMLElement, offset: Offset): void {
    el.style.setProperty('right', offset.right + 'px', 'important');
    el.style.setProperty('bottom', offset.bottom + 'px', 'important');
    el.style.setProperty('left', 'auto', 'important');
    el.style.setProperty('top', 'auto', 'important');
  }

  private loadOffset(): Offset | null {
    try {
      const saved = JSON.parse(localStorage.getItem(HubspotDragComponent.POSITION_KEY) || 'null');
      return saved && typeof saved.right === 'number' && typeof saved.bottom === 'number' ? saved : null;
    } catch {
      return null;
    }
  }
}
